'use client';
import { twMerge } from 'tailwind-merge';
import {
  DownloadOutline,
  ImageOutline,
  TrashOutline
} from '@raresail/react-ionicons';
import { triggerDownload } from '@/lib/utils';
import { resizeImage } from '@/lib/resizeImage';
import { useConfirm } from '@/contexts/dialog-context';
import { FileUploader } from './file-uploader';
import { useCallback } from 'react';
import { useTranslation } from '@/contexts/language-context';

export function ImageInput({
  label,
  className,
  containerClassName,
  labelClassName,
  defaultValue,
  value,
  onChange,
  signUpload,
  deleteFile,
  useBase64 = false,
  ...props
}) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const withConfirm = (fn) => {
    return async () => {
      if (
        await confirm(
          t('shared.imageInput.removeImage.title', 'Remove Image'),
          t(
            'shared.imageInput.removeImage.message',
            'Are you sure you want to remove this image?'
          ),
          t('shared.imageInput.removeImage.confirm', 'Remove'),
          true
        )
      ) {
        fn();
      }
    };
  };

  // Base64 file handler
  const handleBase64Upload = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    fileInput.setAttribute(
      'accept',
      'image/png,image/jpeg,image/webp,image/gif'
    );
    fileInput.addEventListener('change', ({ target }) => {
      const file = target?.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          onChange(reader.result);
        };
        reader.onerror = (err) => {
          console.error('Error reading file:', err);
        };
        fileInput.remove();
      }
    });
    fileInput.click();
  }, [onChange]);

  // Base64 mode - custom implementation
  if (useBase64) {
    return (
      <fieldset
        className={twMerge(
          'fieldset block w-full relative',
          containerClassName
        )}
      >
        {!!label && (
          <legend className={twMerge('fieldset-legend pb-1', labelClassName)}>
            {label}
          </legend>
        )}
        <div
          className={twMerge(
            'w-full h-36 flex flex-col justify-center items-center input input-beautifi p-0 bg-base-100 shadow-beautifi relative',
            className
          )}
        >
          {value && (
            <div className="absolute top-2 right-2 flex flex-col gap-2">
              <button
                className="btn btn-circle btn-error"
                type="button"
                onClick={withConfirm(() => onChange(''))}
              >
                <TrashOutline
                  className="w-6 h-6"
                  role="button"
                  aria-label="Remove Image"
                  info="Remove Image"
                />
              </button>
            </div>
          )}
          {value ? (
            <img
              src={value}
              alt="Image Preview"
              className="block w-full h-full object-contain pointer-events-none"
            />
          ) : (
            <div
              role="button"
              aria-label="Select an image"
              onClick={handleBase64Upload}
              tabIndex={0}
              onKeyUp={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleBase64Upload();
                }
              }}
              className="w-full h-full flex flex-col justify-center items-center p-2 cursor-pointer"
            >
              <ImageOutline className="w-12 h-12 icon-xl" />
              <span className="">
                {t('shared.imageInput.selectImage', 'Select an image…')}
              </span>
            </div>
          )}
          <input value={value || ''} {...props} readOnly type="hidden" />
        </div>
      </fieldset>
    );
  }

  // Regular S3 upload mode
  return (
    <>
      <fieldset
        className={twMerge(
          'fieldset block w-full relative',
          containerClassName
        )}
      >
        {!!label && (
          <legend className={twMerge('fieldset-legend pb-1', labelClassName)}>
            {label}
          </legend>
        )}
        <FileUploader
          containerClassName="w-full h-36 flex flex-col justify-center items-center input input-beautifi p-0 bg-base-100 shadow-beautifi relative"
          className="w-full h-full flex flex-col justify-center items-center p-2"
          defaultValues={
            defaultValue && !Array.isArray(defaultValue)
              ? [defaultValue]
              : defaultValue
          }
          values={value && !Array.isArray(value) ? [value] : value}
          onChange={onChange}
          prepareUpload={signUpload}
          deleteFile={deleteFile}
          accept="image/png,image/jpeg,image/webp,image/gif"
          renderPreviews={([preview], clear) => (
            <>
              {!preview.preview && (
                <div className="absolute top-2 right-2 flex flex-col gap-2">
                  <button
                    className="btn btn-circle btn-error"
                    type="button"
                    onClick={withConfirm(clear)}
                  >
                    <TrashOutline
                      className="w-6 h-6"
                      role="button"
                      aria-label="Remove Image"
                      info="Remove Image"
                    />
                  </button>
                  <button
                    className="btn btn-circle btn-info"
                    type="button"
                    onClick={() =>
                      triggerDownload(
                        resizeImage(preview?.url, null),
                        preview?.url?.replace(/^.+\//, '')
                      )
                    }
                  >
                    <DownloadOutline
                      className="w-6 h-6"
                      role="button"
                      aria-label="Download Original"
                      info="Download Original"
                    />
                  </button>
                </div>
              )}
              <img
                src={resizeImage(preview?.url, 'preview')}
                alt="Image Preview"
                className="block w-full h-full object-contain pointer-events-none"
              />
            </>
          )}
          renderProgress={(progress) => (
            <div className="absolute w-full h-full bg-tremor-content bg-opacity-60 flex justify-center items-center">
              <progress
                className="progress w-56 bg-white border border-base-content outline outline-white"
                value={progress === Infinity ? undefined : progress}
                max="100"
              />
            </div>
          )}
          renderValues={([value]) => (
            <input value={value || ''} {...props} readOnly type="hidden" />
          )}
          placeholder={() => (
            <>
              <ImageOutline className="w-12 h-12 icon-xl" />
              <span className="">
                {t('shared.imageInput.selectImage', 'Select an image…')}
              </span>
            </>
          )}
        />
      </fieldset>
    </>
  );
}
